import partnersLogo from "@/assets/partners_logo.png";
import { formatCurrencyBRL, formatPIXKey } from "./formatters";

export interface ReportMonthRow {
  monthYear: string; // "YYYY-MM"
  label: string; // localized label, e.g. "Março / 2026"
  totalSales: number;
  commission: number;
  ordersCount: number;
}

export interface UserReportInput {
  name: string;
  coupon: string;
  cpf: string | null;
  months: ReportMonthRow[]; // chronological ascending or descending — we display as given
  totalSales: number;
  totalCommission: number;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function buildHtml(input: UserReportInput, logoUrl: string): string {
  const today = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const cpfDisplay = input.cpf ? formatPIXKey(input.cpf) : "—";
  const periodLabel =
    input.months.length > 0
      ? `${input.months[input.months.length - 1].label} – ${input.months[0].label}`
      : "—";

  const rows = input.months
    .map(
      (m) => `
      <tr>
        <td class="month">${escapeHtml(m.label)}</td>
        <td class="num">${escapeHtml(String(m.ordersCount))}</td>
        <td class="num">${escapeHtml(formatCurrencyBRL(m.totalSales))}</td>
        <td class="num">${escapeHtml(formatCurrencyBRL(m.commission))}</td>
      </tr>`
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>Relatório de Vendas — ${escapeHtml(input.name || input.coupon)}</title>
  <style>
    @page { size: A4; margin: 18mm 16mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #111;
      background: #fff;
      font-size: 12pt;
      line-height: 1.45;
    }
    .page { max-width: 760px; margin: 0 auto; padding: 24px 0; }
    header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 2px solid #111;
      padding-bottom: 16px;
      margin-bottom: 28px;
    }
    header img { height: 56px; }
    .doc-meta { text-align: right; font-size: 10pt; color: #444; }
    h1 {
      font-size: 20pt;
      margin: 0 0 4px 0;
      letter-spacing: 0.5px;
    }
    h2 {
      font-size: 11pt;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #666;
      border-bottom: 1px solid #ddd;
      padding-bottom: 4px;
      margin: 24px 0 12px 0;
    }
    .subtitle { color: #555; font-size: 11pt; margin-bottom: 18px; }
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px 24px;
      margin-bottom: 8px;
    }
    .info-label { font-size: 9pt; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
    .info-value { font-size: 12pt; font-weight: 600; margin-bottom: 4px; }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
    }
    th, td {
      padding: 10px 12px;
      border-bottom: 1px solid #e5e5e5;
      text-align: left;
    }
    th {
      background: #f5f5f5;
      font-size: 10pt;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #444;
    }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    td.month { font-weight: 600; }
    .totals {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 24px;
    }
    .total-card {
      border: 1.5px solid #111;
      padding: 14px 18px;
      border-radius: 4px;
    }
    .total-card .label {
      font-size: 9pt;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #555;
    }
    .total-card .value {
      font-size: 18pt;
      font-weight: 700;
      margin-top: 4px;
    }
    .total-card.commission { background: #111; color: #fff; border-color: #111; }
    .total-card.commission .label { color: #bbb; }
    .declaration {
      margin-top: 28px;
      padding: 16px 18px;
      background: #fafafa;
      border-left: 3px solid #111;
      font-size: 11pt;
      text-align: justify;
    }
    .signature {
      margin-top: 56px;
      display: grid;
      grid-template-columns: 1fr;
      gap: 4px;
      text-align: center;
    }
    .signature .line { border-top: 1px solid #333; width: 60%; margin: 0 auto; padding-top: 4px; }
    .signature .name { font-weight: 600; }
    .signature .role { font-size: 10pt; color: #555; }
    footer {
      margin-top: 32px;
      padding-top: 12px;
      border-top: 1px solid #ddd;
      font-size: 9pt;
      color: #777;
      text-align: center;
    }
    @media print {
      .no-print { display: none !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    .print-btn {
      position: fixed;
      top: 16px;
      right: 16px;
      padding: 10px 16px;
      background: #111;
      color: #fff;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12pt;
      font-weight: 600;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    }
  </style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">Imprimir / Salvar PDF</button>
  <div class="page">
    <header>
      <img src="${logoUrl}" alt="Silva Partners" />
      <div class="doc-meta">
        <div><strong>Relatório de Vendas e Comissões</strong></div>
        <div>Emitido em ${escapeHtml(today)}</div>
      </div>
    </header>

    <h1>Demonstrativo de Vendas e Comissões</h1>
    <div class="subtitle">Período: ${escapeHtml(periodLabel)}</div>

    <h2>Dados do Parceiro</h2>
    <div class="info-grid">
      <div>
        <div class="info-label">Nome</div>
        <div class="info-value">${escapeHtml(input.name || "—")}</div>
      </div>
      <div>
        <div class="info-label">CPF</div>
        <div class="info-value">${escapeHtml(cpfDisplay)}</div>
      </div>
      <div>
        <div class="info-label">Cupom</div>
        <div class="info-value" style="font-family: ui-monospace, SFMono-Regular, Menlo, monospace;">${escapeHtml((input.coupon || "—").toUpperCase())}</div>
      </div>
      <div>
        <div class="info-label">Período de referência</div>
        <div class="info-value">${escapeHtml(periodLabel)}</div>
      </div>
    </div>

    <h2>Detalhamento Mensal</h2>
    <table>
      <thead>
        <tr>
          <th>Mês</th>
          <th class="num">Pedidos</th>
          <th class="num">Vendas (R$)</th>
          <th class="num">Comissão (R$)</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="4" style="text-align:center; color:#888; padding:20px;">Sem movimentação no período</td></tr>`}
      </tbody>
    </table>

    <div class="totals">
      <div class="total-card">
        <div class="label">Total de Vendas (3 meses)</div>
        <div class="value">${escapeHtml(formatCurrencyBRL(input.totalSales))}</div>
      </div>
      <div class="total-card commission">
        <div class="label">Total de Comissão (3 meses)</div>
        <div class="value">${escapeHtml(formatCurrencyBRL(input.totalCommission))}</div>
      </div>
    </div>

    <div class="declaration">
      Declaramos, para os devidos fins, que <strong>${escapeHtml(input.name || "—")}</strong>${input.cpf ? `, portador(a) do CPF <strong>${escapeHtml(cpfDisplay)}</strong>,` : ""} é parceiro(a) comercial da <strong>Silva Partners</strong>, vinculado(a) ao cupom <strong>${escapeHtml((input.coupon || "—").toUpperCase())}</strong>, tendo gerado vendas no valor de <strong>${escapeHtml(formatCurrencyBRL(input.totalSales))}</strong> e fazendo jus a comissões no valor total de <strong>${escapeHtml(formatCurrencyBRL(input.totalCommission))}</strong> no período de ${escapeHtml(periodLabel)}.
    </div>

    <div class="signature">
      <div class="line">Silva Partners</div>
      <div class="role">Documento gerado eletronicamente</div>
    </div>

    <footer>
      Silva Partners · Relatório emitido em ${escapeHtml(today)} · Documento confidencial
    </footer>
  </div>
  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 300);
    });
  </script>
</body>
</html>`;
}

export function openUserReport(input: UserReportInput): void {
  const logoUrl = window.location.origin + partnersLogo;
  const html = buildHtml(input, logoUrl);
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) {
    throw new Error("Pop-up bloqueado. Habilite pop-ups para gerar o relatório.");
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
